import { customSeriesDefaultOptions } from 'lightweight-charts'

function hexToRgba(hex, alpha) {
  if (!hex || hex === 'transparent') return `rgba(0,0,0,${alpha})`
  if (hex.startsWith('rgba(') || hex.startsWith('rgb(')) return hex
  const h = hex.startsWith('#') ? hex.slice(1) : hex
  if (h.length < 6) return hex
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return `rgba(${r},${g},${b},${alpha.toFixed(3)})`
}

function textColorFor(bg) {
  if (!bg || bg === 'transparent') return '#f8fafc'
  const h = bg.startsWith('#') ? bg.slice(1) : bg
  if (h.length < 6) return '#f8fafc'
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return '#f8fafc'
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.55 ? '#0f172a' : '#f8fafc'
}

/**
 * LabelOverlaySeries — etiquetas de texto dibujadas directamente en el canvas
 * del chart, sincronizadas frame a frame con las velas, zonas y líneas.
 *
 * Evita la capa DOM (IndicatorOverlayLabels) que flotaba desincronizada al
 * hacer pan/zoom. Cada punto puede contener varias etiquetas ancladas al mismo
 * tiempo/precio (lightweight-charts no permite filas de datos repetidas).
 *
 * Soporta:
 * - textAlign: 'left' | 'center' | 'right' (por defecto 'center')
 * - xOffset: desplazamiento horizontal en píxeles desde la vela ancla
 * - xOffsetBars: desplazamiento horizontal en anchos de vela
 * - plaqueStyle: 'plaque' | 'plain'. En modo 'plain' solo se dibuja el texto,
 *   sin fondo, pill, roundRect ni borde; es el estilo de etiquetas de
 *   estructura CHoCH/BOS e ICT/SMC Quantum.
 * - Anti-colisión vertical: si dos etiquetas se solapan, la de menor prioridad se
 *   desplaza fuera del área ocupada.
 */
class LabelOverlayPaneRenderer {
  constructor() {
    this._data = null
    this._options = null
  }

  update(data, options) {
    this._data = data
    this._options = options
  }

  _fontSize() {
    const m = (this._options?.font || '').match(/(\d+)px/)
    return m ? Math.max(8, parseInt(m[1], 10)) : 12
  }

  draw(target, priceConverter) {
    if (!this._data || !this._options) return
    const { bars, visibleRange } = this._data
    if (!visibleRange || !bars || bars.length === 0) return

    const from = Math.max(0, Math.floor(visibleRange.from))
    const to = Math.min(bars.length - 1, Math.ceil(visibleRange.to))
    if (from > to) return

    target.useMediaCoordinateSpace(scope => {
      const ctx = scope.context
      const { width, height } = scope.mediaSize
      const font = this._options.font || 'bold 12px -apple-system, BlinkMacSystemFont, "Trebuchet MS", Roboto, Ubuntu, sans-serif'
      const fontSize = this._fontSize()
      const seriesPlaqueStyle = this._options?.plaqueStyle || 'plaque'

      ctx.font = font
      ctx.textBaseline = 'middle'

      const visibleLabels = []
      for (let i = from; i <= to; i++) {
        const bar = bars[i]
        const d = bar?.originalData
        if (!d || !Array.isArray(d.labels) || d.labels.length === 0) continue

        // Ancho de una vela en píxeles, para desplazamientos expresados en barras.
        const barWidth = (i < bars.length - 1 && bars[i + 1].x != null && bar.x != null)
          ? Math.abs(bars[i + 1].x - bar.x)
          : (i > 0 && bars[i - 1].x != null && bar.x != null)
            ? Math.abs(bar.x - bars[i - 1].x)
            : 0

        for (const label of d.labels) {
          if (!label || !label.text || label.price == null || Number.isNaN(label.price)) continue

          const y = priceConverter(label.price)
          if (y == null || Number.isNaN(y)) continue
          if (y < -40 || y > height + 40) continue

          const x = label.leftAnchored ? 6 : bar.x
          if (x == null || Number.isNaN(x)) continue
          if (!label.leftAnchored && (x < -80 || x > width + 80)) continue

          visibleLabels.push({ ...label, x, y, barWidth })
        }
      }

      visibleLabels.sort((a, b) => {
        const p = (b.priority ?? 0) - (a.priority ?? 0)
        if (p !== 0) return p
        // A igual prioridad, el evento más antiguo se coloca primero; el más
        // reciente se desplaza si colisiona (CHoCH/BOS rule: "desplazar el más
        // reciente un nivel más arriba/abajo").
        return (a.time ?? 0) - (b.time ?? 0)
      })

      const placed = []
      for (const label of visibleLabels) {
        const isLeft = label.leftAnchored
        const align = label.textAlign || (isLeft ? 'left' : 'center')
        const xOffsetPx = (label.xOffset ?? 0) + (label.xOffsetBars ?? 0) * (label.barWidth || 0)
        const baseOffset = label.offset ?? 6
        let dx = label.x + xOffsetPx
        let dy = label.y

        if (label.anchor === 'above') dy -= baseOffset
        else if (label.anchor === 'below') dy += baseOffset

        const isPlain = (label.plaqueStyle ?? seriesPlaqueStyle) === 'plain'

        const hPadding = isPlain ? 0 : 7
        const margin = isPlain ? 1 : 4
        const lineHeight = isPlain ? fontSize + 2 : fontSize + 6
        const cornerRadius = isPlain ? 0 : 4

        const text = String(label.text)
        const textWidth = ctx.measureText(text).width
        const rectW = textWidth + hPadding * 2
        const rectH = lineHeight + margin * 2

        let rectX
        if (align === 'left') rectX = dx
        else if (align === 'right') rectX = dx - rectW
        else rectX = dx - rectW / 2

        const rectY = dy - rectH / 2

        let shift = 0
        let finalY = dy
        let finalRectY = rectY
        const step = label.anchor === 'above' ? -rectH : rectH

        for (let attempt = 0; attempt < 8; attempt++) {
          const rect = { x: rectX, y: finalRectY, w: rectW, h: rectH }
          const overlap = placed.some(p => rect.x < p.x + p.w && rect.x + rect.w > p.x && rect.y < p.y + p.h && rect.y + rect.h > p.y)
          if (!overlap) break
          shift += step
          finalY = dy + shift
          finalRectY = finalY - rectH / 2
        }

        // Clamp dentro del rango vertical visible del pane: la anti-colisión
        // de arriba puede desplazar una etiqueta (típicamente Long, anchor
        // 'below') fuera del borde inferior del chart -- sin este clamp
        // queda cortada/invisible en vez de simplemente re-solapada (hallazgo
        // de Marc, revisión visual). Preferimos visibilidad a no-solape
        // perfecto en el borde.
        const clampMin = rectH / 2 + 2
        const clampMax = height - rectH / 2 - 2
        if (clampMax > clampMin) {
          if (finalY < clampMin) { finalY = clampMin; finalRectY = finalY - rectH / 2 }
          else if (finalY > clampMax) { finalY = clampMax; finalRectY = finalY - rectH / 2 }
        }

        const finalRect = { x: rectX, y: finalRectY, w: rectW, h: rectH }
        if (placed.some(p => finalRect.x < p.x + p.w && finalRect.x + finalRect.w > p.x && finalRect.y < p.y + p.h && finalRect.y + finalRect.h > p.y)) continue
        if (finalY < -40 || finalY > height + 40) continue

        if (isPlain) {
          ctx.fillStyle = label.textColor || label.color || '#f8fafc'
        } else {
          const bgColor = label.backgroundColor || hexToRgba(label.color || '#475569', label.backgroundAlpha ?? 0.85)
          ctx.fillStyle = bgColor
          if (ctx.roundRect) {
            ctx.beginPath()
            ctx.roundRect(finalRect.x, finalRect.y, finalRect.w, finalRect.h, cornerRadius)
            ctx.fill()
          } else {
            ctx.fillRect(finalRect.x, finalRect.y, finalRect.w, finalRect.h)
          }
          ctx.fillStyle = label.textColor || textColorFor(bgColor)
        }

        ctx.textAlign = align === 'left' ? 'left' : (align === 'right' ? 'right' : 'center')
        let textX = dx
        if (align === 'left') textX = dx + hPadding
        else if (align === 'right') textX = dx - hPadding
        ctx.fillText(text, textX, finalY)

        placed.push(finalRect)
      }
    })
  }
}

export class LabelOverlaySeries {
  constructor() {
    this._renderer = new LabelOverlayPaneRenderer()
  }

  renderer() {
    return this._renderer
  }

  update(data, seriesOptions) {
    this._renderer.update(data, seriesOptions)
  }

  priceValueBuilder(plotRow) {
    const d = plotRow?.labels
    if (!Array.isArray(d)) return []
    return d.map(l => l?.price).filter(p => p != null && Number.isFinite(p))
  }

  isWhitespace(data) {
    return data == null || !Array.isArray(data.labels) || data.labels.length === 0
  }

  defaultOptions() {
    return {
      ...customSeriesDefaultOptions,
      color: 'transparent',
      lineWidth: 0,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      font: 'bold 12px -apple-system, BlinkMacSystemFont, "Trebuchet MS", Roboto, Ubuntu, sans-serif',
    }
  }
}
